import { createSlice } from "@reduxjs/toolkit";
import OrganizationAccess from "../models/OrganizationAccess";

// True when the stored { organization, related } is already the same selection (by org id),
// so we can skip the write and avoid handing subscribers a new map reference on every re-select.
// Compares by ORGANIZATIONID because inside an Immer reducer the existing nested objects are
// draft proxies and never === the raw payload objects.
const isSameSelectedEntry = (existing, next) => {
    if (!existing || !next) {
        return false;
    }
    if (
        existing.organization?.ORGANIZATIONID !==
        next.organization?.ORGANIZATIONID
    ) {
        return false;
    }
    const a = existing.related || [];
    const b = next.related || [];
    return (
        a.length === b.length &&
        a.every(
            (org, index) => org?.ORGANIZATIONID === b[index]?.ORGANIZATIONID
        )
    );
};

const organizationSlice = createSlice({
    name: "organizations",
    initialState: {
        selectedOrganizations: {},

        organizationsAccess: {},
        lastSelectedOrganization: null,
        totalParentOrganizations: 0,
        currentSelectedOrganization: null,
        lastSelectedEmployee: null,
        organizationsCount: null,
        selectedOrganizationsId: {},
    },
    reducers: {
        setTotalParentOrganization(state, action) {
            state.totalParentOrganizations = action.payload;
        },
        setOrganizationsCount(state, action) {
            state.organizationsCount = action.payload;
        },
        addSelectedOrganization(state, action) {
            const next = {
                organization: action.payload.organization,
                related: action.payload.related,
            };
            if (
                !isSameSelectedEntry(
                    state.selectedOrganizations[action.payload.id],
                    next
                )
            ) {
                state.selectedOrganizations[action.payload.id] = next;
            }
        },

        setSelectedOrgainzationsId(state, action) {
            state.selectedOrganizationsId = action.payload;
        },

        addOrganizationAccessInfo(state, action) {
            if (Array.isArray(action.payload)) {
                const accessInfos = action.payload;

                accessInfos.forEach((info) => {
                    const formattedInfo = OrganizationAccess._format(info);
                    const normalizedOrganizationId =
                        formattedInfo.ORGANIZATIONID;

                    if (normalizedOrganizationId) {
                        state.organizationsAccess[normalizedOrganizationId] =
                            formattedInfo;
                    }

                    // Keep compatibility for any existing callers using the raw id key.
                    if (
                        info?.OrganizationId !== null &&
                        info?.OrganizationId !== undefined
                    ) {
                        state.organizationsAccess[info.OrganizationId] =
                            formattedInfo;
                    }
                });
            }
        },
        addLastSelectedOrganization(state, action) {
            state.lastSelectedOrganization = action.payload;
        },
        addLastSelectedEmployee(state, action) {
            state.lastSelectedEmployee = action.payload;
        },
        clearSelectedOrganizations(state, action) {
            state.selectedOrganizations = {};
            state.organizationsAccess = {};
            state.lastSelectedOrganization = null;
            state.totalParentOrganizations = 0;
            state.currentSelectedOrganization = null;
        },
        setCurrentSelectedOrganization(state, action) {
            state.currentSelectedOrganization =
                state.selectedOrganizations[action.payload]?.organization;
        },
    },
});

export const {
    setTotalParentOrganization,
    setOrganizationsCount,
    addSelectedOrganization,

    addOrganizationAccessInfo,
    addLastSelectedOrganization,
    clearSelectedOrganizations,
    setCurrentSelectedOrganization,
    addLastSelectedEmployee,
    setSelectedOrgainzationsId,
} = organizationSlice.actions;

export default organizationSlice.reducer;
